import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import {
  EmptyState,
  ErrorNotice,
  Notice,
  Spinner,
  formatDate,
} from '../components/ui';
import type { AwaitingMentor, Mentorship, MentorSuggestion } from '../types';

const STATUS_LABELS: Record<string, string> = {
  proposed: 'proposed',
  active: 'running',
  completed: 'finished',
  declined: 'declined',
};

/**
 * Pairing mentors with mentees.
 *
 * The matcher suggests and explains; it never assigns. Picking someone is
 * always a click by a committee member, because the data model is right that
 * no algorithm knows Bob and Dave fell out over a swarm in 2019 — and the
 * distances here are district-level anyway, so the human eye on "oh, she's
 * just up the road from him" is doing real work.
 */
export function MentoringPage() {
  const [mentorships, setMentorships] = useState<Mentorship[] | null>(null);
  const [awaiting, setAwaiting] = useState<AwaitingMentor[] | null>(null);
  const [matching, setMatching] = useState<AwaitingMentor | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [flash, setFlash] = useState<string | null>(null);

  function load() {
    api
      .get<{ mentorships: Mentorship[]; awaiting_mentor: AwaitingMentor[] }>('/mentoring')
      .then((result) => {
        setMentorships(result.mentorships);
        setAwaiting(result.awaiting_mentor);
      })
      .catch(setError);
  }

  useEffect(load, []);

  function announce(message: string) {
    setFlash(message);
    window.setTimeout(() => setFlash(null), 5000);
  }

  const open = mentorships?.filter((m) => m.status === 'proposed' || m.status === 'active') ?? [];
  const closed = mentorships?.filter((m) => m.status === 'completed' || m.status === 'declined') ?? [];

  return (
    <>
      <div className="page-head">
        <h1>Mentoring</h1>
        <div className="head-actions">
          <Link to="/mentoring/courses" className="button-link">
            Courses
          </Link>
        </div>
      </div>

      {flash ? <Notice kind="success">{flash}</Notice> : null}
      <ErrorNotice error={error} />

      {matching !== null ? (
        <SuggestionPicker
          mentee={matching}
          onClose={() => setMatching(null)}
          onPaired={(name) => {
            setMatching(null);
            announce(`Proposed ${name}. Confirm it below to introduce them to each other.`);
            load();
          }}
        />
      ) : null}

      <div className="card">
        <h2>Waiting for a mentor</h2>
        <p className="muted small">
          Anyone who has been on a course and has nobody paired with them.
        </p>
        {awaiting === null ? <Spinner label="Loading" /> : null}
        {awaiting !== null && awaiting.length === 0 ? (
          <EmptyState title="Nobody is waiting">
            Everyone who has been on a course has a mentor. Add course attendees under{' '}
            <Link to="/mentoring/courses">Courses</Link>.
          </EmptyState>
        ) : null}
        {awaiting !== null && awaiting.length > 0 ? (
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Member</th>
                <th scope="col">Where</th>
                <th scope="col">Course</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {awaiting.map((person) => (
                <tr key={person.id}>
                  <td data-label="Member">
                    <Link to={`/members/${person.id}`} className="member-name">
                      {person.first_name} {person.last_name}
                    </Link>
                  </td>
                  <td data-label="Where">
                    {person.town ?? '—'}
                    {person.postcode ? <span className="muted"> {person.postcode}</span> : null}
                  </td>
                  <td data-label="Course">
                    {person.course_name} {person.course_year}
                    {person.completed_on ? null : <span className="muted block small">still in progress</span>}
                  </td>
                  <td data-label="">
                    <button type="button" className="primary" onClick={() => setMatching(person)}>
                      Find a mentor
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <div className="card">
        <h2>Pairings</h2>
        {mentorships === null ? <Spinner label="Loading" /> : null}
        {mentorships !== null && open.length === 0 ? (
          <EmptyState title="No pairings yet" />
        ) : null}
        {open.map((mentorship) => (
          <MentorshipRow
            key={mentorship.id}
            mentorship={mentorship}
            onChanged={(message) => {
              announce(message);
              load();
            }}
            onError={setError}
          />
        ))}
      </div>

      {closed.length > 0 ? (
        <div className="card">
          <h2>Finished</h2>
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Mentor</th>
                <th scope="col">Mentee</th>
                <th scope="col">Ran</th>
                <th scope="col">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {closed.map((mentorship) => (
                <tr key={mentorship.id}>
                  <td data-label="Mentor">{mentorship.mentor_name}</td>
                  <td data-label="Mentee">{mentorship.mentee_name}</td>
                  <td data-label="Ran">
                    {formatDate(mentorship.started_on)} – {formatDate(mentorship.ended_on)}
                  </td>
                  <td data-label="Outcome">
                    <span className={`pill pill-mentorship-${mentorship.status}`}>
                      {STATUS_LABELS[mentorship.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

function MentorshipRow({
  mentorship,
  onChanged,
  onError,
}: {
  mentorship: Mentorship;
  onChanged: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(mentorship.notes ?? '');
  const [editingNotes, setEditingNotes] = useState(false);

  async function setStatus(status: string, message: string) {
    setBusy(true);
    onError(null);
    try {
      const result = await api.put<{ mentorship: Mentorship }>(`/mentoring/${mentorship.id}/status`, {
        status,
      });
      const queued = result.mentorship.introductions_queued ?? 0;
      onChanged(
        queued > 0
          ? `${message} Both of them have been sent an introduction.`
          : message,
      );
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function saveNotes() {
    setBusy(true);
    try {
      await api.put(`/mentoring/${mentorship.id}/notes`, { notes });
      setEditingNotes(false);
      onChanged('Note saved.');
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pairing">
      <div className="pairing-people">
        <span>
          <Link to={`/members/${mentorship.mentor_member_id}`}>{mentorship.mentor_name}</Link>
          <span className="muted block small">mentor{mentorship.mentor_town ? `, ${mentorship.mentor_town}` : ''}</span>
        </span>
        <span className="pairing-arrow" aria-hidden="true">→</span>
        <span>
          <Link to={`/members/${mentorship.mentee_member_id}`}>{mentorship.mentee_name}</Link>
          <span className="muted block small">mentee{mentorship.mentee_town ? `, ${mentorship.mentee_town}` : ''}</span>
        </span>
        <span className={`pill pill-mentorship-${mentorship.status}`}>
          {STATUS_LABELS[mentorship.status]}
        </span>
      </div>

      {mentorship.matched_by_name ? (
        <p className="muted small">
          Matched by {mentorship.matched_by_name}
          {mentorship.started_on ? `, running since ${formatDate(mentorship.started_on)}` : ''}.
        </p>
      ) : null}

      {editingNotes ? (
        <div className="field">
          <label htmlFor={`notes-${mentorship.id}`}>Committee notes</label>
          <textarea
            id={`notes-${mentorship.id}`}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <span className="hint">Never shown to either of them.</span>
          <div className="actions">
            <button type="button" disabled={busy} onClick={() => void saveNotes()}>
              Save note
            </button>
            <button type="button" onClick={() => { setNotes(mentorship.notes ?? ''); setEditingNotes(false); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="muted small">
          {mentorship.notes ? <>{mentorship.notes} · </> : null}
          <button type="button" className="link" onClick={() => setEditingNotes(true)}>
            {mentorship.notes ? 'Edit note' : 'Add a note'}
          </button>
        </p>
      )}

      <div className="actions">
        {mentorship.status === 'proposed' ? (
          <>
            <button type="button" className="primary" disabled={busy} onClick={() => void setStatus('active', 'Pairing confirmed.')}>
              {busy ? 'Confirming…' : 'Confirm and introduce them'}
            </button>
            <button type="button" disabled={busy} onClick={() => void setStatus('declined', 'Pairing declined.')}>
              Not this one
            </button>
          </>
        ) : null}
        {mentorship.status === 'active' ? (
          <button type="button" disabled={busy} onClick={() => void setStatus('completed', 'Pairing finished.')}>
            Mark as finished
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SuggestionPicker({
  mentee,
  onClose,
  onPaired,
}: {
  mentee: AwaitingMentor;
  onClose: () => void;
  onPaired: (name: string) => void;
}) {
  const [suggestions, setSuggestions] = useState<MentorSuggestion[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    api
      .get<{ suggestions: MentorSuggestion[] }>(`/mentoring/suggestions/${mentee.id}`)
      .then((result) => setSuggestions(result.suggestions))
      .catch(setError);
  }, [mentee.id]);

  async function propose(suggestion: MentorSuggestion) {
    setBusy(suggestion.member_id);
    setError(null);
    try {
      await api.post('/mentoring', {
        mentor_member_id: suggestion.member_id,
        mentee_member_id: mentee.id,
      });
      onPaired(suggestion.name);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card suggestion-picker">
      <div className="page-head card-head">
        <h2>
          A mentor for {mentee.first_name} {mentee.last_name}
        </h2>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="muted small">
        Nearest first, and only people who have said they are willing. These are suggestions —
        pick whoever actually suits, and nothing is sent until you confirm the pairing.
      </p>

      <ErrorNotice error={error} />
      {suggestions === null && error === null ? <Spinner label="Looking" /> : null}

      {suggestions !== null && suggestions.length === 0 ? (
        <EmptyState title="Nobody available">
          No member has offered to mentor yet. The willingness toggle is on each member's record.
        </EmptyState>
      ) : null}

      {suggestions?.map((suggestion) => (
        <div className="suggestion" key={suggestion.member_id}>
          <div>
            <Link to={`/members/${suggestion.member_id}`} className="member-name">
              {suggestion.name}
            </Link>
            <span className="muted block small">
              {suggestion.town ?? 'whereabouts unknown'} · {suggestion.reason}
            </span>
          </div>
          <button
            type="button"
            className={suggestion.has_room ? 'primary' : ''}
            disabled={busy !== null}
            onClick={() => void propose(suggestion)}
          >
            {busy === suggestion.member_id ? 'Pairing…' : 'Pair them up'}
          </button>
        </div>
      ))}
    </div>
  );
}
